import React, { useState } from 'react';
import { Contact } from '../../../types/Contact';
import { UserCircle, Pencil, X, Check } from 'lucide-react';
import { updateContactCore } from '../../../lib/contactsCore';
import { supabase } from '../../../lib/supabase';
import { copyField } from '../utils/contactHelpers';

interface PersonalInfoSectionProps {
    formData: Contact;
    setFormData: React.Dispatch<React.SetStateAction<Contact>>;
    onSave: () => void;
    showNotification?: (message: string, type: 'success' | 'error') => void;
}

export default function PersonalInfoSection({
    formData,
    setFormData,
    onSave,
    showNotification
}: PersonalInfoSectionProps) {
    const [isPersonalEditing, setIsPersonalEditing] = useState(false);

    const savePersonal = async () => {
        if (!formData.id) return;
        try {
            await updateContactCore(supabase as any, String(formData.id), {
                email: (formData.email || formData.email_1_value || null) as any,
                mobile: (formData.mobile || formData.phone_1_value || null) as any,
                linkedin_url: (formData.linkedin_url || null) as any,
            });
            setIsPersonalEditing(false);
            onSave();
        } catch (e) {
            showNotification?.('Failed to save personal info', 'error');
        }
    };

    return (
        <div className="bg-gray-900 rounded-xl">
            <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                <div className="flex items-center space-x-2">
                    <UserCircle className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-white">Personal</h3>
                </div>
                <div className="flex items-center gap-2">
                    {isPersonalEditing ? (
                        <>
                            <button onClick={() => setIsPersonalEditing(false)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Cancel" aria-label="Cancel">
                                <X className="w-4 h-4" />
                            </button>
                            <button onClick={savePersonal} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save" aria-label="Save">
                                <Check className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setIsPersonalEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Edit" aria-label="Edit">
                            <Pencil className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
            <div className="px-5 pb-5">
                <div className="bg-gray-800 rounded-lg p-4">
                    {!isPersonalEditing ? (
                        <ul className="text-sm text-gray-300 space-y-2">
                            <li>
                                <span className="text-gray-400">Personal Email: </span>{' '}
                                {(formData.email || formData.email_1_value) ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Email', formData.email || formData.email_1_value, showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {formData.email || formData.email_1_value}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                            <li>
                                <span className="text-gray-400">Mobile: </span>{' '}
                                {(formData.mobile || formData.phone_1_value) ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Mobile', formData.mobile || formData.phone_1_value, showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {formData.mobile || formData.phone_1_value}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                            <li>
                                <span className="text-gray-400">LinkedIn: </span>{' '}
                                {formData.linkedin_url ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('LinkedIn', formData.linkedin_url, showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors text-left break-all"
                                    >
                                        {formData.linkedin_url}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                        </ul>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Personal Email</label>
                                <input value={formData.email || ''} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Mobile</label>
                                <input value={formData.mobile || ''} onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">LinkedIn</label>
                                <input value={formData.linkedin_url || ''} onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}